import { useAppNavigate } from "../../../../hooks/useAppNavigate";
import React, { FC } from "react";
import { StackedBarChartWidget } from "./StackedBarChartWidget";
import { Typography } from "@mui/material";
import { ActiveProgramStats } from "common/build/api-parameters/users";
import { useFetch } from "../../../../core/components/crud/contexts/fetch.context";
import { VTextLink } from "../../../common/VTextLink";
import { VictoryBar, VictoryLabel, VictoryTheme, VictoryTooltip } from "victory";
import { useAuth } from "../../../../contexts/auth.context";
import { Alert } from "@mui/material";

export const ActiveProgrammeSessionsBarWidget: FC<{ isFacilitator: boolean }> = ({ isFacilitator }) => {
    type LabelInfo = {
        programmeId: number;
        classroomId?: number | null;
    };

    const { item } = useFetch<ActiveProgramStats>();
    const { hasPermission, user } = useAuth();
    const hasProgrammes = item?.programmes && item.programmes.length > 0 ? true : false;
    const programmes = item?.programmes;
    const { push } = useAppNavigate();

    const labelData: Record<string, LabelInfo | undefined> = {};

    const filteredProgrammes = programmes?.filter(user?.isSchool ? (p) => p.classroomId : () => true) ?? [];

    const chartData = [
        filteredProgrammes.map((programme, id) => {
            const completedCount = programme.Sessions.filter((s) => s.state === "Complete").length;
            return {
                x: `${programme.name} (${id + 1})`,
                y: (completedCount / (programme._count?.Sessions ?? 0)) * 100,
                classroomId: programme.classroomId,
                programmeId: programme.id,
                sessionsComplete: completedCount,
            };
        }),

        filteredProgrammes.map((programme, id) => {
            const completedCount = programme.Sessions.filter((s) => s.state === "Complete").length;
            return {
                x: `${programme.name} (${id + 1})`,
                y: 100 - (completedCount / (programme._count?.Sessions ?? 0)) * 100,
                programmeId: programme.id,
                classroomId: programme.classroomId,
                sessionsRemaining: (programme._count?.Sessions ?? 0) - completedCount,
            };
        }),
    ];
    const legend = [
        { name: "Completed", symbol: { fill: "#1EAE8C", type: "square" } },
        { name: "Remaining", symbol: { fill: "#131739", type: "square" } },
    ];
    if (filteredProgrammes.length > 0) {
        for (let id = 0; id < filteredProgrammes.length; id++) {
            const programme = filteredProgrammes[id];
            labelData[`${programme.name} (${id + 1})`] = {
                programmeId: programme.id,
                classroomId: programme.classroomId,
            };
        }
    }
    return (
        <StackedBarChartWidget
            programmeCount={programmes?.length ?? 0}
            legend={legend}
            xAxisLabelComponent={
                <VictoryLabel
                    style={[
                        {
                            fontSize: 10,
                            fontWeight: "bold",
                            cursor: "pointer",
                        },
                    ]}
                />
            }
            xAxisEvents={[
                {
                    target: "tickLabels",
                    eventHandlers: {
                        onClick: (_event: unknown, data: { text: string; data: never[] }) => {
                            const pathname = user?.isSchool
                                ? `/classrooms/view/${labelData[data?.text]?.classroomId}/programmes/view/${labelData[data?.text]?.programmeId}/`
                                : `/programmes/view/${labelData[data?.text]?.programmeId}/`;
                            const search = user?.isSchool
                                ? ""
                                : hasPermission("ProgrammeReportView")
                                  ? "?tab=data"
                                  : "";
                            push(`${pathname}${search}`);
                        },
                    },
                },
            ]}
            barCharts={chartData.map((chartData, index) => (
                <VictoryBar
                    groupComponent={<g style={{ cursor: "pointer" }} />}
                    key={index}
                    theme={VictoryTheme.material}
                    data={chartData}
                    events={[
                        {
                            target: "data",
                            eventHandlers: {
                                onClick: (_event, data) => {
                                    user?.isSchool
                                        ? push(
                                              `/classrooms/view/${data.datum.classroomId}/programmes/view/${data.datum.programmeId}`,
                                          )
                                        : push(`/programmes/view/${data.datum.programmeId}`);
                                },
                            },
                        },
                    ]}
                    labels={({ datum }) => {
                        return `${datum.x}: \n ${
                            datum.sessionsRemaining
                                ? `${datum.sessionsRemaining} ${
                                      datum.sessionsRemaining === 1 ? `Session` : `Sessions`
                                  } Remaining`
                                : `${datum.sessionsComplete} ${
                                      datum.sessionsComplete === 1 ? `Session` : `Sessions`
                                  } Complete`
                        }`;
                    }}
                    labelComponent={
                        <VictoryTooltip
                            cornerRadius={() => 4}
                            flyoutPadding={4}
                            flyoutStyle={{
                                fill: "white",
                                stroke: "#e3e3e3",
                                fontSize: 6,
                            }}
                            constrainToVisibleArea
                        />
                    }
                />
            ))}
            spinWhileFetching
            header="Active Programme Sessions"
            subHeader={<Alert severity="info">Click the programme name to view programme data</Alert>}
            hasNoData={!hasProgrammes}
            noDataMessage={
                <>
                    <Typography variant={"body1"}>
                        Follow progress on active sessions and easily see how far along and how much is left of a
                        programme
                    </Typography>
                    <Typography variant={"body2"}>
                        To see this you must first{" "}
                        {user?.isSchool ? (
                            "schedule a programme inside a classroom "
                        ) : isFacilitator ? (
                            "schedule a programme "
                        ) : (
                            <VTextLink to="/programmes/add">schedule a programme </VTextLink>
                        )}
                    </Typography>
                </>
            }
        />
    );
};
